/**
 * SubjectInput Component
 * Email subject line input with variable support
 * Allows insertion of {{variableName}} syntax variables
 */

import { useState, useRef, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Braces,
  Search,
  User,
  Building2,
  Calendar,
  Mail,
} from 'lucide-react'
import { DEFAULT_VARIABLES, type Variable, type VariableCategory } from '../VariablePicker'

// ============================================
// Types
// ============================================

export interface SubjectInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  customVariables?: Variable[]
}

// ============================================
// Constants
// ============================================

const CATEGORY_ICONS: Record<VariableCategory, React.ElementType> = {
  User: User,
  Company: Building2,
  Date: Calendar,
  Contact: Mail,
  Financial: Building2,
  Document: Building2,
  Custom: Braces,
}

const CATEGORY_COLORS: Record<VariableCategory, string> = {
  User: 'text-blue-500',
  Company: 'text-purple-500',
  Date: 'text-green-500',
  Contact: 'text-rose-500',
  Financial: 'text-amber-500',
  Document: 'text-cyan-500',
  Custom: 'text-neutral-500',
}

// Filter to email-relevant variables
const EMAIL_VARIABLE_CATEGORIES: VariableCategory[] = [
  'User',
  'Contact',
  'Company',
  'Date',
  'Custom',
]

// ============================================
// Variable Tag Component
// ============================================

interface VariableTagProps {
  name: string
  onRemove: () => void
  disabled?: boolean
}

function VariableTag({ name }: VariableTagProps) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-mono">
      <Braces className="h-3 w-3" />
      {name}
    </span>
  )
}

// ============================================
// SubjectInput Component
// ============================================

export function SubjectInput({
  value,
  onChange,
  placeholder = 'Enter email subject...',
  disabled = false,
  className,
  customVariables = [],
}: SubjectInputProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Extract variables from the current value
  const extractedVariables = useMemo(() => {
    const regex = /\{\{(\w+)\}\}/g
    const matches: string[] = []
    let match: RegExpExecArray | null
    while ((match = regex.exec(value)) !== null) {
      const varName = match[1]
      if (varName && !matches.includes(varName)) {
        matches.push(varName)
      }
    }
    return matches
  }, [value])

  // Get email-relevant variables
  const emailVariables = useMemo(() => {
    const filtered = DEFAULT_VARIABLES.filter((v) =>
      EMAIL_VARIABLE_CATEGORIES.includes(v.category)
    )
    return [...filtered, ...customVariables]
  }, [customVariables])

  // Filter variables based on search
  const filteredVariables = useMemo(() => {
    if (!search.trim()) return emailVariables
    const searchLower = search.toLowerCase()
    return emailVariables.filter(
      (v) =>
        v.name.toLowerCase().includes(searchLower) ||
        v.displayName.toLowerCase().includes(searchLower) ||
        v.description?.toLowerCase().includes(searchLower)
    )
  }, [emailVariables, search])

  // Group by category
  const groupedVariables = useMemo(() => {
    const groups: Partial<Record<VariableCategory, Variable[]>> = {}
    filteredVariables.forEach((v) => {
      if (!groups[v.category]) {
        groups[v.category] = []
      }
      groups[v.category]!.push(v)
    })
    return groups
  }, [filteredVariables])

  // Handle variable insertion
  const handleInsertVariable = useCallback(
    (variable: Variable) => {
      const input = inputRef.current
      if (!input) return

      const variableText = `{{${variable.name}}}`
      const start = input.selectionStart ?? value.length
      const end = input.selectionEnd ?? value.length

      const newValue =
        value.substring(0, start) + variableText + value.substring(end)
      onChange(newValue)

      setIsOpen(false)
      setSearch('')

      // Restore focus and cursor position
      setTimeout(() => {
        input.focus()
        const newCursorPos = start + variableText.length
        input.setSelectionRange(newCursorPos, newCursorPos)
      }, 0)
    },
    [value, onChange]
  )

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'rounded-xl pr-10',
              extractedVariables.length > 0 && 'font-mono text-sm'
            )}
          />
          {extractedVariables.length > 0 && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                <Braces className="h-3.5 w-3.5" />
                {extractedVariables.length}
              </span>
            </div>
          )}
        </div>

        {/* Variable Picker Button */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={disabled}
              className="h-10 w-10 rounded-xl shrink-0"
            >
              <Braces className="h-4 w-4 text-blue-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-72 p-0 rounded-xl shadow-lg"
            align="end"
            sideOffset={5}
          >
            {/* Search */}
            <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <Input
                  type="text"
                  placeholder="Search variables..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 rounded-lg text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Variable List */}
            <ScrollArea className="h-[240px]">
              <div className="p-2">
                {filteredVariables.length === 0 ? (
                  <div className="text-center py-6 text-neutral-500">
                    <Braces className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No variables found</p>
                  </div>
                ) : (
                  Object.entries(groupedVariables).map(
                    ([category, variables]) => {
                      if (!variables?.length) return null
                      const Icon = CATEGORY_ICONS[category as VariableCategory]
                      const color = CATEGORY_COLORS[category as VariableCategory]

                      return (
                        <div key={category} className="mb-2 last:mb-0">
                          <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                            <Icon className={cn('h-3.5 w-3.5', color)} />
                            {category}
                          </div>
                          <div className="space-y-0.5">
                            {variables.map((variable) => (
                              <button
                                key={variable.name}
                                type="button"
                                onClick={() => handleInsertVariable(variable)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                              >
                                <span className="text-neutral-700 dark:text-neutral-300">
                                  {variable.displayName}
                                </span>
                                <code className="text-xs text-neutral-400 dark:text-neutral-500 font-mono ml-auto">
                                  {`{{${variable.name}}}`}
                                </code>
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    }
                  )
                )}
              </div>
            </ScrollArea>

            {/* Hint */}
            <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
              <p className="text-xs text-neutral-500 text-center">
                Click to insert at cursor position
              </p>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Variable Preview */}
      {extractedVariables.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-1">
            Variables:
          </span>
          {extractedVariables.map((varName) => (
            <VariableTag
              key={varName}
              name={varName}
              onRemove={() => {
                const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g')
                onChange(value.replace(regex, ''))
              }}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default SubjectInput
